import { useMemo, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileCheck2,
  Loader2,
  Save,
  Send,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { ArbiterShell } from "@/components/ArbiterShell";
import { FormationGate } from "@/components/FormationGate";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useIsArbiterOrAdmin } from "@/contexts/AuthContext";
import { classifyApiError } from "@/hooks/useApiErrorState";
import {
  type Art20ValidationResponse,
  type SentenceSectionType,
  createDecision,
  generateSentenceSection,
  getApiErrorDetail,
  validateArt20,
} from "@/lib/api";

/**
 * Brouillon de sentence assiste par IA (Art. 20 AUA + Art. 14 AI Act).
 *
 * Les 8 sections obligatoires (Art. 20 AUA 2017) sont editables a la
 * main par l'arbitre. Sur les 5 sections supportees par le moteur
 * (motifs, dispositif, procedure, pretentions, moyens), un bouton
 * "Generation IA" appelle `/api/v1/sentences/generate-section`. Le
 * texte est INSERE comme suggestion : l'arbitre garde la main et doit
 * valider explicitement (HITL Art. 14) avant tout export.
 *
 * Au moment de "Creer la decision", on assemble les 8 sections en un
 * Markdown structure et on POST `/api/v1/decisions/` (type FINAL_AWARD
 * ou PARTIAL_AWARD), puis on redirige vers /cases/:id/decisions ou
 * l'arbitre pourra signer.
 */

interface SentenceSection {
  id: string;
  label: string;
  required: boolean;
  /** Mapping vers le type d'IA. null = pas de generation IA disponible. */
  iaType: SentenceSectionType | null;
  placeholder: string;
}

const SECTIONS: SentenceSection[] = [
  {
    id: "identification_parties",
    label: "Identification des parties",
    required: true,
    iaType: null,
    placeholder:
      "Demandeur : [Denomination, siege social, representant…]\nDefendeur : [Denomination, siege social, representant…]",
  },
  {
    id: "designation_arbitres",
    label: "Designation des arbitres",
    required: true,
    iaType: null,
    placeholder:
      "Tribunal arbitral compose de :\n- [Arbitre unique / President / Co-arbitre]",
  },
  {
    id: "date_lieu_sentence",
    label: "Date et lieu de la sentence",
    required: true,
    iaType: null,
    placeholder: "Sentence rendue a [lieu] le [date].",
  },
  {
    id: "resume_pretentions",
    label: "Resume des pretentions",
    required: true,
    iaType: "pretentions",
    placeholder:
      "Le demandeur conclut a…\nLe defendeur conclut a…",
  },
  {
    id: "resume_moyens",
    label: "Resume des moyens",
    required: true,
    iaType: "moyens",
    placeholder:
      "Au soutien de leurs pretentions, les parties invoquent…",
  },
  {
    id: "expose_procedure",
    label: "Expose de la procedure",
    required: true,
    iaType: "procedure",
    placeholder:
      "La procedure d'arbitrage a ete initiee par requete en date du… (rappel des etapes).",
  },
  {
    id: "motivation",
    label: "Motivation",
    required: true,
    iaType: "motifs",
    placeholder:
      "Sur la competence du tribunal : …\nSur le fond du litige : …",
  },
  {
    id: "dispositif",
    label: "Dispositif",
    required: true,
    iaType: "dispositif",
    placeholder:
      "PAR CES MOTIFS, le tribunal arbitral, statuant en dernier ressort :\n1. Declare…\n2. Condamne…",
  },
];

type ConfidenceByIaType = Partial<Record<SentenceSectionType, number>>;

export default function SentenceDraft() {
  const [, params] = useRoute<{ caseId: string }>("/cases/:caseId/sentence");
  const [, setLocation] = useLocation();
  const isArbiterOrAdmin = useIsArbiterOrAdmin();
  const caseId = params?.caseId ? Number.parseInt(params.caseId, 10) : NaN;

  const [contents, setContents] = useState<Record<string, string>>(() =>
    Object.fromEntries(SECTIONS.map((s) => [s.id, ""])),
  );
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [confidenceByIa, setConfidenceByIa] = useState<ConfidenceByIaType>({});
  const [art20, setArt20] = useState<Art20ValidationResponse | null>(null);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hitlAccepted, setHitlAccepted] = useState(false);

  const activeSection = useMemo(
    () => SECTIONS.find((s) => s.id === activeId) ?? SECTIONS[0],
    [activeId],
  );

  if (!Number.isFinite(caseId)) {
    return (
      <ArbiterShell title="Brouillon de sentence">
        <div className="p-6 text-sm text-destructive">
          Identifiant de dossier invalide.
        </div>
      </ArbiterShell>
    );
  }

  if (!isArbiterOrAdmin) {
    return (
      <ArbiterShell title="Brouillon de sentence">
        <div className="p-6 max-w-xl">
          <div
            className="rounded-md border p-4 text-sm"
            style={{
              borderColor:
                "color-mix(in oklch, var(--alert-orange) 35%, transparent)",
              background:
                "color-mix(in oklch, var(--alert-orange) 10%, transparent)",
              color: "var(--alert-orange)",
            }}
          >
            La redaction assistee par IA est reservee aux profils Arbitre
            et Admin (UserScope.REDACTION).
          </div>
        </div>
      </ArbiterShell>
    );
  }

  const setSection = (id: string, value: string) => {
    setContents((c) => ({ ...c, [id]: value }));
  };

  const handleGenerate = async (section: SentenceSection) => {
    if (!section.iaType) return;
    setGeneratingId(section.id);
    try {
      const res = await generateSentenceSection({
        case_file_id: caseId,
        section_type: section.iaType,
      });
      setSection(section.id, res.generated_text);
      setConfidenceByIa((c) => ({
        ...c,
        [section.iaType as SentenceSectionType]: res.confidence_score,
      }));
      toast.success(
        `Section "${section.label}" generee par IA${res.simulated ? " (mode simulation)" : ""}.`,
      );
    } catch (err) {
      const c = classifyApiError(err);
      toast.error(
        c.kind !== "none"
          ? c.detail
          : (getApiErrorDetail(err) ?? "Generation IA indisponible."),
      );
    } finally {
      setGeneratingId(null);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const res = await validateArt20(contents);
      setArt20(res);
      if (res.is_compliant) {
        toast.success("Sentence conforme Art. 20 AUA.");
      } else {
        toast.warning(
          `${res.missing_sections.length} section(s) manquante(s) — Art. 20 AUA.`,
        );
      }
    } catch (err) {
      toast.error(
        getApiErrorDetail(err) ?? "Validation Art. 20 indisponible.",
      );
    } finally {
      setValidating(false);
    }
  };

  const assembleMarkdown = (): string => {
    const lines: string[] = [];
    for (const section of SECTIONS) {
      const body = contents[section.id]?.trim();
      if (!body) continue;
      lines.push(`## ${section.label}`, "", body, "");
    }
    return lines.join("\n");
  };

  const handleCreateDecision = async (
    type: "FINAL_AWARD" | "PARTIAL_AWARD",
  ) => {
    if (!hitlAccepted) {
      toast.error(
        "Validation humaine (HITL Art. 14 AI Act) obligatoire avant creation.",
      );
      return;
    }
    if (art20 && !art20.is_compliant) {
      toast.error(
        "Conformite Art. 20 AUA non atteinte — corrigez les sections manquantes.",
      );
      return;
    }
    setSubmitting(true);
    try {
      const decision = await createDecision({
        case_file_id: caseId,
        decision_type: type,
        title:
          type === "FINAL_AWARD"
            ? "Sentence finale (brouillon IA-assiste)"
            : "Sentence partielle (brouillon IA-assiste)",
        content_markdown: assembleMarkdown(),
      });
      toast.success("Decision creee, redirection vers la signature…");
      setLocation(`/cases/${caseId}/decisions#decision-${decision.id}`);
    } catch (err) {
      toast.error(
        getApiErrorDetail(err) ?? "Creation de la decision impossible.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const filledCount = SECTIONS.filter((s) =>
    contents[s.id]?.trim(),
  ).length;

  return (
    <ArbiterShell title="Brouillon de sentence">
      <FormationGate parcours="ARBITRE" feature="sentence_draft">
      <div className="flex-1 px-4 sm:px-6 py-6 sm:py-8 max-w-6xl mx-auto w-full space-y-4">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link
              href={`/cases/${caseId}/decisions`}
              className="text-caption-arbitre inline-flex items-center gap-1"
              style={{ color: "var(--proof-gold)" }}
            >
              <ArrowLeft size={12} /> Decisions du dossier
            </Link>
            <h1 className="text-h1-arbitre mt-1">
              GSF Sentence Companion
            </h1>
            <p
              className="text-body-arbitre mt-1"
              style={{ color: "var(--arbitre-slate-300)" }}
            >
              Redaction assistee — 8 sections Art. 20 AUA. Chaque
              generation IA est tracee (Art. 12-13) et requiert votre
              validation humaine (Art. 14) avant export.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleValidate()}
              disabled={validating}
            >
              {validating ? (
                <Loader2 className="animate-spin mr-1.5" size={14} />
              ) : (
                <ShieldCheck size={14} className="mr-1.5" />
              )}
              Verifier Art. 20
            </Button>
          </div>
        </header>

        {art20 && (
          <div
            className="rounded-md border p-3 text-sm"
            style={{
              borderColor: art20.is_compliant
                ? "color-mix(in oklch, var(--proof-gold) 35%, transparent)"
                : "color-mix(in oklch, var(--alert-orange) 35%, transparent)",
              background: art20.is_compliant
                ? "color-mix(in oklch, var(--proof-gold) 8%, transparent)"
                : "color-mix(in oklch, var(--alert-orange) 8%, transparent)",
              color: art20.is_compliant
                ? "var(--proof-gold)"
                : "var(--alert-orange)",
            }}
          >
            <div className="flex items-center gap-2 font-semibold">
              {art20.is_compliant ? (
                <CheckCircle2 size={14} />
              ) : (
                <AlertTriangle size={14} />
              )}
              Conformite Art. 20 AUA :{" "}
              {Math.round(art20.compliance_score * 100)}% (
              {art20.filled_sections}/{art20.total_required_sections}
              sections)
            </div>
            {art20.missing_sections.length > 0 && (
              <ul className="mt-1.5 list-disc pl-5 text-xs">
                {art20.missing_sections.map((s) => (
                  <li key={s}>Manquant : {s}</li>
                ))}
              </ul>
            )}
            {art20.warnings.length > 0 && (
              <ul className="mt-1.5 list-disc pl-5 text-xs">
                {art20.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
          {/* Nav sections */}
          <nav className="gsf-surface p-2 h-fit sticky top-4">
            <div
              className="text-caption-arbitre px-2 py-1.5"
              style={{ color: "var(--proof-gold)" }}
            >
              Sections Art. 20 ({filledCount}/{SECTIONS.length})
            </div>
            <ul className="space-y-0.5">
              {SECTIONS.map((s) => {
                const filled = !!contents[s.id]?.trim();
                const active = s.id === activeId;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(s.id)}
                      className="w-full text-left px-2 py-1.5 rounded text-xs flex items-start gap-2"
                      style={{
                        background: active
                          ? "color-mix(in oklch, var(--proof-gold) 12%, transparent)"
                          : "transparent",
                        color: active
                          ? "var(--proof-gold)"
                          : "var(--arbitre-slate-100)",
                        border: active
                          ? "1px solid color-mix(in oklch, var(--proof-gold) 30%, transparent)"
                          : "1px solid transparent",
                      }}
                    >
                      <span
                        className="mt-0.5 shrink-0 w-1.5 h-1.5 rounded-full"
                        style={{
                          background: filled
                            ? "var(--proof-gold)"
                            : "var(--graphite-600)",
                        }}
                      />
                      <span className="flex-1">{s.label}</span>
                      {s.iaType && (
                        <Sparkles
                          size={10}
                          style={{ color: "var(--ai-violet)" }}
                          aria-label="Generation IA disponible"
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Editeur */}
          <div className="space-y-3">
            <section className="gsf-surface p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h2
                  className="text-h3-arbitre"
                  style={{ color: "var(--arbitre-slate-100)" }}
                >
                  {activeSection.label}
                  {activeSection.required && (
                    <span
                      className="ml-2 text-xs"
                      style={{ color: "var(--alert-orange)" }}
                    >
                      requise
                    </span>
                  )}
                </h2>
                {activeSection.iaType && (
                  <Button
                    size="sm"
                    onClick={() => void handleGenerate(activeSection)}
                    disabled={generatingId === activeSection.id}
                    style={{
                      background: "var(--ai-violet)",
                      color: "var(--arbitre-slate-100)",
                    }}
                  >
                    {generatingId === activeSection.id ? (
                      <>
                        <Loader2
                          className="animate-spin mr-1.5"
                          size={14}
                        />
                        Generation…
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} className="mr-1.5" />
                        Generation IA
                      </>
                    )}
                  </Button>
                )}
              </div>

              <Textarea
                value={contents[activeSection.id]}
                onChange={(e) =>
                  setSection(activeSection.id, e.target.value)
                }
                placeholder={activeSection.placeholder}
                rows={16}
                className="resize-y font-mono text-sm"
              />

              {activeSection.iaType &&
                confidenceByIa[activeSection.iaType] !== undefined && (
                  <div
                    className="rounded-md p-2 text-xs flex items-center gap-2"
                    style={{
                      background:
                        "color-mix(in oklch, var(--ai-violet) 8%, transparent)",
                      color: "var(--arbitre-slate-300)",
                    }}
                  >
                    <Sparkles
                      size={11}
                      style={{ color: "var(--ai-violet)" }}
                    />
                    Derniere generation IA — confiance{" "}
                    {Math.round(
                      (confidenceByIa[activeSection.iaType] ?? 0) * 100,
                    )}
                    % — relisez et editez avant validation (HITL Art. 14
                    obligatoire).
                  </div>
                )}
            </section>

            {/* Validation HITL + submit */}
            <section
              className="gsf-surface p-4 space-y-3"
              style={{
                borderTop: "2px solid var(--proof-gold)",
              }}
            >
              <h3
                className="text-h3-arbitre flex items-center gap-2"
                style={{ color: "var(--arbitre-slate-100)" }}
              >
                <FileCheck2 size={16} style={{ color: "var(--proof-gold)" }} />
                Validation et creation de la decision
              </h3>

              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={hitlAccepted}
                  onChange={(e) => setHitlAccepted(e.target.checked)}
                  className="mt-1 shrink-0"
                />
                <span style={{ color: "var(--arbitre-slate-300)" }}>
                  Je certifie avoir relu integralement les sections
                  generees par l'IA et en assume la paternite intellectuelle
                  (Human-In-The-Loop, AI Act Art. 14). Les sections seront
                  assemblees en un brouillon de decision (FINAL_AWARD
                  ou PARTIAL_AWARD) que je pourrai ensuite signer
                  electroniquement.
                </span>
              </label>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const blob = new Blob([assembleMarkdown()], {
                      type: "text/markdown",
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `brouillon-sentence-dossier-${caseId}.md`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <Save size={14} className="mr-1.5" />
                  Exporter brouillon (.md)
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    void handleCreateDecision("PARTIAL_AWARD")
                  }
                  disabled={submitting || !hitlAccepted}
                >
                  <Send size={14} className="mr-1.5" />
                  Creer sentence partielle
                </Button>
                <Button
                  size="sm"
                  onClick={() => void handleCreateDecision("FINAL_AWARD")}
                  disabled={submitting || !hitlAccepted}
                  style={{
                    background: "var(--proof-gold)",
                    color: "var(--graphite-900)",
                  }}
                >
                  {submitting ? (
                    <Loader2 className="animate-spin mr-1.5" size={14} />
                  ) : (
                    <Send size={14} className="mr-1.5" />
                  )}
                  Creer sentence finale
                </Button>
              </div>

              <p
                className="text-[11px] italic"
                style={{ color: "var(--arbitre-slate-500)" }}
              >
                Une fois cree, le brouillon devient une decision modifiable
                sur la page Decisions. La signature electronique
                (eIDAS + ancrage Polygon) intervient lors d'une etape
                ulterieure et derriere votre confirmation explicite.
              </p>
            </section>
          </div>
        </div>
      </div>
      </FormationGate>
    </ArbiterShell>
  );
}
